import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

const ProgressBar = () => {
  const [activeDot, setActiveDot] = useState(0); // Which dot/content is active
  const [animationStopped, setAnimationStopped] = useState(false); // If true, stop the sequence
  const controls = useAnimation();
  const [isInView, setIsInView] = useState(false); // Tracks if the component is in the viewport
  const containerRef = useRef(null);

  // Array of different content for each step
  const contents = [
    <div className="h-[65vh]">
      <div
        className="w-full h-[25vh] bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: "url('/ImageSlider/Background6.png')" }}
      >
        {/* Image is set via background to easily control its size */}
      </div>

      {/* Text Section */}
      <div className="text-left mt-2 pl-2">
        Yarn quality plays a vital role in determining the final product
        quality. So, We stock and procure a{" "}
        <strong>wide range of quality yarns to meet diverse needs.</strong> Our
        skilled professionals ensure precision knitting for superior results. We
        efficiently manage small and large-scale orders,
        <strong>
          {" "}
          maintaining strict quality control at every stage, from yarn selection
          to fabric production,
        </strong>{" "}
        ensuring consistency, durability, and a premium finish.
      </div>
    </div>,
    <div className="h-[65vh]">
      {" "}
      <div
        className="w-full h-[25vh] bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: "url('/ImageSlider/Background7.png')" }}
      >
        {/* Image is set via background to easily control its size */}
      </div>
      {/* Text Section */}
      <div className="text-left mt-4 pl-2">
        Using{" "}
        <strong> high quality dyes and our in-house dyeing facility</strong>, we
        achieve vibrant colors. Our advanced processing techniques ensure
        consistent color saturation lasting durability. With{" "}
        <strong>precision and premium dye selection</strong>, every product
        meets rigorous standards. Our state-of-the-art facilities and skilled
        professionals handle orders seamlessly, delivering fabrics that are up
        to the mark.{" "}
      </div>
    </div>,

    <div className="h-[65vh]">
      {" "}
      <div
        className="w-full h-[25vh] bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: "url('/ImageSlider/Background8.png')" }}
      >
        {/* Image is set via background to easily control its size */}
      </div>
      {/* Text Section */}
      <div className="text-left pl-2 mt-6  ">
        Our comprehensive fabric printing services encompass various techniques,
        including{" "}
        <span className="inline font-bold">
          rotary screen printing, digital printing, flat-bed printing, Block
          printing and sublimation.
        </span>{" "}
        Each method is tailored to deliver top-notch quality and meeting diverse
        design needs while enhancing the overall aesthetic appeal of the fabric.
      </div>
    </div>,
    <div className="h-[65vh]">
      <div
        className="w-full h-[25vh]  bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: "url('/ImageSlider/Background9.png')" }}
      >
        {/* Image is set via background to easily control its size */}
      </div>

      {/* Text Section */}
      <div className="text-left pl-2 mt-4">
        We offer a<strong> comprehensive range of packaging options</strong>{" "}
        designed to meet your unique needs. Our options include custom labels,
        high-quality tags, and protective covers, all tailored to your
        specifications. Whether you require personalized branding or standard
        packaging, our team works diligently to pack each order with the utmost
        care.
      </div>
    </div>,
    <div className="h-[65vh]">
      {" "}
      <div
        className="w-full h-[25vh] bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: "url('/ImageSlider/Background10.png')" }}
      >
        {/* Image is set via background to easily control its size */}
      </div>
      {/* Text Section */}
      <div className="text-left pl-2">
        At Madura, we prioritize{" "}
        <strong>handling logistics with utmost care and efficiency.</strong> We
        ensure that your orders are delivered smoothly and on time, whether{" "}
        <strong>locally or internationally.</strong> If needed, we use the
        available fast and reliable mediums to ensure swift and secure delivery,
        always keeping your priorities in mind. Our team is dedicated to making
        sure that your shipments reach you with the highest level of
        reliability.
      </div>
    </div>,
  ];

  // Widths for each dot (so the progress bar lines up properly)
  const dotWidths = ["0%", "25%", "50%", "75%", "100%"];

  // Handle dot click
  const handleDotClick = (index) => {
    // Stop any ongoing animation
    setAnimationStopped(true);
    controls.stop();

    // Jump the progress bar to the clicked dot’s width
    controls.set({ width: dotWidths[index] });

    // Update the active dot (so we see the correct content)
    setActiveDot(index);
  };

  // Automatic progress bar sequence
  useEffect(() => {
    // If animation has been stopped or not in view, do not run/restart the sequence
    if (animationStopped || !isInView) return;

    // For safely canceling the sequence if the component unmounts
    let isCancelled = false;

    const runSequence = async () => {
      // Dot #0
      setActiveDot(0);
      await controls.start({
        width: "25%",
        transition: { duration: 5, ease: "linear" },
      });
      if (isCancelled) return;
      setActiveDot(1);

      // Dot #1
      await controls.start({
        width: "50%",
        transition: { duration: 5, ease: "linear" },
      });
      if (isCancelled) return;
      setActiveDot(2);

      // Dot #2
      await controls.start({
        width: "75%",
        transition: { duration: 5, ease: "linear" },
      });
      if (isCancelled) return;
      setActiveDot(3);

      // Dot #3
      await controls.start({
        width: "100%",
        transition: { duration: 5, ease: "linear" },
      });
      if (isCancelled) return;
      setActiveDot(4);

      // Wait 3 seconds before resetting
      await new Promise((resolve) => setTimeout(resolve, 5000));
      if (isCancelled) return;

      // Reset the animation and start again
      controls.set({ width: "0%" });
      runSequence();
    };

    runSequence();

    // Cleanup if component unmounts
    return () => {
      isCancelled = true;
      controls.stop(); // Stop any ongoing animations
    };
  }, [controls, animationStopped, isInView]);

  // Intersection Observer to detect when the progress bar is in the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Optional: only trigger once, so unobserve once intersecting:
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.7 } // 70% must be visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // dotlabels start

  // dotlabels end

  return (
    <div class="relative p-6">
      {/* Active Step Content */}

      <div
        ref={containerRef}
        className="w-full h-auto relative flex flex-col  "
      >
        <div className="w-full  text-center ">{contents[activeDot]}</div>
        {/* Progress Bar Container */}
        {/* <div className="w-full  h-1 bg-[#F5F5F5] relative "> */}
        {/* Animated progress line */}
        {/* <motion.div
            className="h-[2px] w-full   bg-[#14394c] absolute top-0 left-0"
            initial={{ width: "0%" }}
            animate={controls}
          /> */}
        <div className="w-full h-1 bg-[#F5F5F5] relative mb-6">
          <motion.div
            className="h-[2px] w-full bg-[#14394c]"
            initial={{ width: "0%" }}
            animate={controls}
          />

          {/* Dots */}
          <div className="">
            <div className="flex  justify-between  items-center w-full     absolute -top-6  ">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  // Outer circle
                  className="relative  w-4 h-4 bg-[#F5F5F5] mt-4 rounded-full flex items-center justify-center cursor-pointer"
                  // On dot click, jump to that dot and stop animation
                  onClick={() => handleDotClick(index)}
                >
                  {/* Inner circle */}
                  <div
                    className={`w-3 h-3 rounded-full ${
                      activeDot >= index ? "bg-[#14394c]" : "bg-white"
                    } transition-colors duration-500`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
